import ImageDropUpload from '../components/ImageDropUpload';
import config from '../config.json';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 멤버 수정 페이지
function EditMember() {
  const navigate = useNavigate();
  const { id } = useParams();
  const imageUploadRef = useRef();
  const [formData, setFormData] = useState({
    member_id: '',
    name: '',
    name_kor: '',
    status: 'phd',
    research_interest: '',
    email: '',
    homepage: '',
    github: '',
    tel: '',
    info: '',
    image_link: '/images/default.jpg'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Status 목록 (수정 가능한 항목만)
  const statuses = [
    { value: 'phd', label: 'Ph.D Student' },
    { value: 'master', label: 'M.S. Student' },
    { value: 'intern', label: 'Intern' }
  ];

  // 기존 데이터 로드
  useEffect(() => {
    fetchMember();
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/members/${id}`);
      const data = await response.json();

      if (data.success) {
        setFormData({
          member_id: data.data.member_id || '',
          name: data.data.name || '',
          name_kor: data.data.name_kor || '',
          status: data.data.status || 'master',
          research_interest: data.data.research_interest || '',
          email: data.data.email || '',
          homepage: data.data.homepage || '',
          github: data.data.github || '',
          tel: data.data.tel || '',
          info: data.data.info || '',
          image_link: data.data.image_link || '/images/default.jpg'
        });
      } else {
        setError('Failed to load member data');
      }
    } catch (err) {
      console.error('Error fetching member:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // 입력값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 필수 필드 검증
    if (!formData.name || !formData.email) {
      setError('Name and Email are required');
      return;
    }

    // 이미지 업로드 (선택된 파일이 있을 때만)
    const uploadedLink = await imageUploadRef.current?.upload();
    if (uploadedLink === false) return; // 업로드 실패
    const finalFormData = uploadedLink
      ? { ...formData, image_link: uploadedLink }
      : formData;

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/members/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalFormData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/members');
        }, 1500);
      } else {
        setError(data.error || 'Failed to update member');
      }
    } catch (err) {
      console.error('Error updating member:', err);
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  // 졸업생으로 설정
  const handleSetAlumni = async () => {
    if (!window.confirm('이 멤버를 졸업생으로 설정하시겠습니까?\n\n졸업생은 Members 페이지에 표시되지 않지만 서버에는 남아 있습니다.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const alumniData = { ...formData, status: 'alumni' };

      const response = await fetch(`${API_URL}/members/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(alumniData)
      });

      const data = await response.json();

      if (data.success) {
        alert('졸업생으로 설정되었습니다.');
        navigate('/members');
      } else {
        setError(data.error || 'Failed to set as alumni');
      }
    } catch (err) {
      console.error('Error setting alumni:', err);
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading member data...</p>
      </div>
    );
  }

  // 성공 메시지
  if (success) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <h2 style={{ color: 'green' }}>Members 페이지가 성공적으로 갱신되었습니다.</h2>
        <p>Members 페이지로 돌아가는 중...</p>
      </div>
    );
  }

  return (
    <div className="site-content">
      <h2 className="section-title">Edit Member</h2>

      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Name (English) */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            이름 (영문) <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="{영어 성: 모두 대문자} {영어 이름: 첫 글자만 대문자}"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Name (Korean) */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            이름 (한글)
          </label>
          <input
            type="text"
            name="name_kor"
            value={formData.name_kor}
            onChange={handleChange}
            
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Email */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            이메일 <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Status */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            과정 <span style={{ color: 'red' }}>*</span>
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            {statuses.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Research Interest */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            관심 연구 분야
          </label>
          <textarea
            name="research_interest"
            value={formData.research_interest}
            onChange={handleChange}
            rows="3"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Member ID */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            ID
          </label>
          <input
            type="text"
            name="member_id"
            value={formData.member_id}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Homepage */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            홈페이지 URL
          </label>
          <input
            type="url"
            name="homepage"
            value={formData.homepage}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* GitHub */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            깃허브 URL
          </label>
          <input
            type="url"
            name="github"
            value={formData.github}
            onChange={handleChange}
            placeholder="https://github.com/{깃허브 ID}"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Tel */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            연락처
          </label>
          <input
            type="tel"
            name="tel"
            value={formData.tel}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Image Upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            이미지
          </label>
          <ImageDropUpload
            ref={imageUploadRef}
            uploadDir="member"
            imageBaseUrl={`${config.images.serverUrl}${config.images.basePath}`}
            currentImageLink={formData.image_link}
          />
        </div>

        {/* Additional Info */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            추가 정보
          </label>
          <textarea
            name="info"
            value={formData.info}
            onChange={handleChange}
            rows="5"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ marginBottom: '30px' }}>
          {/* 졸업생으로 설정 버튼 */}
          <button
            type="button"
            onClick={handleSetAlumni}
            disabled={saving}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              marginBottom: '20px'
            }}
          >
            졸업생으로 설정
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/members')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#ddd',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Updating...' : '변경 사항 저장'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditMember;
